import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TextHuntResult({ user }) {
  const { texthuntId, studentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [texthunt, setTexthunt] = useState(null);

  useEffect(() => {
    loadResult();
  }, [texthuntId, studentId]);

  const loadResult = async () => {
    try {
      // Fetch the result
      const resultRes = await axios.get(`${BACKEND_URL}/api/texthunt/${texthuntId}/result/${studentId}`);
      setResult(resultRes.data);

      // Fetch the texthunt details
      const texthuntRes = await axios.get(`${BACKEND_URL}/api/texthunt/${texthuntId}`);
      setTexthunt(texthuntRes.data);
      
      // Check if graded (only for students viewing their own results)
      if (studentId === user.id) {
        const hasShortAnswers = texthuntRes.data.questions.some(q => q.question_type === 'short_answer');
        
        if (hasShortAnswers) {
          // Check if all short answers are graded
          let allGraded = true;
          for (const answer of resultRes.data.answers) {
            const question = texthuntRes.data.questions.find(q => q.question_id === answer.question_id);
            if (question?.question_type === 'short_answer' && 
                answer.ai_score === null && answer.manual_score === null) {
              allGraded = false;
              break;
            }
          }
          
          if (!allGraded) {
            alert('⏳ Este TextHunt aún no ha sido corregido por tu profesor.\n\nPodrás ver tus resultados cuando esté completamente corregido.');
            navigate('/dashboard');
            return;
          }
        }
      }
    } catch (error) {
      console.error('Error loading result:', error);
      alert('Error al cargar los resultados');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mx-auto mb-4"></div>
          <p className="text-xl font-semibold">Cargando resultados...</p>
        </div>
      </div>
    );
  }

  if (!result || !texthunt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-gray-600">No se encontraron resultados</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="mt-4 px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Volver al Dashboard
          </button>
        </div>
      </div>
    );
  }

  const totalQuestions = texthunt.questions.length;
  const scorePercentage = Math.round((result.total_score / texthunt.total_points) * 100);

  // Motivational messages
  const getMotivationalMessage = (percentage) => {
    if (percentage >= 90) return "¡Excelente trabajo! 🌟 Demuestras un dominio sobresaliente del tema.";
    if (percentage >= 80) return "¡Muy bien! 👏 Has captado muy bien los conceptos principales.";
    if (percentage >= 70) return "¡Buen trabajo! 💪 Vas por buen camino, sigue así.";
    if (percentage >= 60) return "Bien hecho 📚 Con un poco más de práctica mejorarás aún más.";
    return "Sigue intentándolo 🎯 La práctica te ayudará a mejorar.";
  };

  // Map answers to questions
  const answersWithQuestions = result.answers.map((answer) => {
    const question = texthunt.questions.find(q => q.question_id === answer.question_id);
    return { ...answer, question };
  });

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="text-center">
            <div className="text-6xl mb-4">
              {scorePercentage >= 80 ? '🌟' : scorePercentage >= 60 ? '👍' : '📚'}
            </div>
            <h1 className="text-3xl font-bold text-gray-800 mb-2">¡Actividad Corregida!</h1>
            <h2 className="text-xl text-gray-600 mb-4">{texthunt.title}</h2>
            
            {/* Score Display */}
            <div className="bg-purple-100 rounded-lg p-6 mb-4">
              <div className="text-5xl font-bold text-purple-600 mb-2">
                {result.total_score} / {texthunt.total_points}
              </div>
              <div className="text-lg text-gray-700 mb-2">
                {scorePercentage}% - {totalQuestions} preguntas
              </div>
              <div className="text-base text-gray-600 italic">
                {getMotivationalMessage(scorePercentage)}
              </div>
            </div>

            {/* Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-semibold"
              >
                ← Volver al Dashboard
              </button>
            </div>
          </div>
        </div>

        {/* Detailed Results */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-2xl font-bold text-gray-800 mb-6">Detalle de Respuestas</h3>
          
          <div className="space-y-6">
            {answersWithQuestions.map((answer, index) => (
              <div 
                key={answer.question_id} 
                className={`border-l-4 p-4 rounded-lg ${
                  answer.is_correct || (answer.ai_score !== null && answer.ai_score >= 70)
                    ? 'border-green-500 bg-green-50'
                    : 'border-red-500 bg-red-50'
                }`}
              >
                {/* Question */}
                <div className="mb-3">
                  <span className="font-semibold text-gray-700">Pregunta {index + 1}: </span>
                  <span className="text-gray-800">{answer.question?.question_text}</span>
                </div>

                {/* Multiple Choice */}
                {answer.question?.question_type === 'multiple_choice' && (
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-semibold">Tu respuesta: </span>
                      <span className={answer.is_correct ? 'text-green-700 font-semibold' : 'text-red-700'}>
                        {answer.question.options[parseInt(answer.answer)]}
                        {answer.is_correct ? ' ✓' : ' ✗'}
                      </span>
                    </div>
                    {!answer.is_correct && (
                      <div className="text-sm">
                        <span className="font-semibold">Respuesta correcta: </span>
                        <span className="text-green-700 font-semibold">
                          {answer.question.options[answer.question.correct_answer_index]} ✓
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Short Answer */}
                {answer.question?.question_type === 'short_answer' && (
                  <div className="space-y-2">
                    <div className="text-sm">
                      <span className="font-semibold">Tu respuesta: </span>
                      <div className="mt-1 p-2 bg-white rounded border">
                        {answer.answer}
                      </div>
                    </div>
                    
                    {answer.question.model_answer && (
                      <div className="text-sm">
                        <span className="font-semibold">Respuesta modelo (referencia): </span>
                        <div className="mt-1 p-2 bg-white rounded border text-gray-600 italic">
                          {answer.question.model_answer}
                        </div>
                      </div>
                    )}

                    {/* AI Feedback */}
                    {answer.ai_score !== null && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">Evaluación IA:</span>
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            answer.ai_score >= 70 ? 'bg-green-200 text-green-800' : 'bg-yellow-200 text-yellow-800'
                          }`}>
                            {answer.ai_score}/100
                          </span>
                        </div>
                        {answer.ai_feedback && (
                          <div className="text-sm text-gray-700 p-2 bg-blue-50 rounded border border-blue-200">
                            💡 {answer.ai_feedback}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Manual Feedback */}
                    {answer.manual_score !== null && (
                      <div className="mt-2">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">Evaluación del Profesor:</span>
                          <span className="px-2 py-1 bg-purple-200 text-purple-800 rounded text-xs font-semibold">
                            {answer.manual_score}/100
                          </span>
                        </div>
                        {answer.manual_feedback && (
                          <div className="text-sm text-gray-700 p-2 bg-purple-50 rounded border border-purple-200">
                            📝 {answer.manual_feedback}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Time and Points */}
                <div className="mt-3 flex gap-4 text-xs text-gray-600">
                  <span>⏱️ Tiempo: {answer.time_taken}s</span>
                  <span>🎯 Puntos: {answer.question?.points}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Back Button */}
        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/dashboard')}
            className="px-8 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-semibold"
          >
            ← Volver al Dashboard
          </button>
        </div>
      </div>
    </div>
  );
}

export default TextHuntResult;
